// ── Project Insights: a grid of read-only widgets (TPT590) ──
//
// #/projects/:id/insights. The sprint burndown (GET /projects/:id/sprints/:sprintId/burndown), the
// closed sprints' velocity (GET /projects/:id/sprints/velocity), both charted by
// lib/burndown-chart.js, and the project's task count per status (GET /projects/:id/tasks/facets
// `status_counts`, in the order and colours of loadStatuses()). Each widget is a card in
// .insights-grid; the burndown card owns its sprint selector. See tt-web-insights.md.

import { api, getSprintBurndown, getSprintVelocity } from '../api.js';
import { checkAuth, getUser } from '../auth.js';
import { initLayout, getContentEl, updateLayoutContext } from '../layout.js';
import { loadStatuses, statusLabel, statusBadgeAttrs } from '../lib/statuses.js';
import { loadProject } from '../lib/project.js';
import { can } from '../lib/permissions.js';
import { sprintsEnabled } from '../lib/sprints-enabled.js';
import { groupLabelPlural, groupLabelSingular, isGeneratedGroupName } from '../lib/task-group-label.js';
import { sprintSchedule } from '../lib/sprint-dates.js';
import { todayIso } from '../lib/due-tasks.js';
import {
  MAX_CHART_DAYS,
  escapeText,
  pickCurrentSprint,
  sprintChartBlock,
  isSprintDatesMissing,
  buildBurndownGeometry,
  renderBurndownSvg,
  burndownSummary,
  burndownReadout,
  defaultReadoutIndex,
  nearestIndex,
  formatAmount,
  formatValue,
  dayLabel,
  velocityBars,
  renderVelocitySvg,
  velocityReadout,
  nearestGroup,
} from '../lib/burndown-chart.js';
import { getNavigationId, getQueryParams, setQueryParams } from '../router.js';

// Closed sprints the Velocity widget charts.
const VELOCITY_SPRINTS = 12;

let state = freshState();

function freshState() {
  return {
    navId: null,
    projectId: null,
    project: null,
    user: null,
    sprints: [],
    sprintsError: null,
    statuses: [],
    statusCounts: null,
    statusError: null,
    selectedNumber: null,
    // status: 'idle' (nothing to fetch) | 'loading' | 'ready' | 'error'
    burndown: { status: 'idle', data: null, error: null },
    burndownSeq: 0,
    geom: null,
    chartWidth: 0,
    hoverIndex: -1,
    // status: 'loading' | 'ready' | 'error'
    velocity: { status: 'loading', rows: [], error: null },
    velocityGeom: null,
    velocityWidth: 0,
    velocityHover: -1,
    resizeObserver: null,
    resizeFrame: 0,
  };
}

function resetState() {
  state = freshState();
}

// Still the mount that `owner` belongs to — the same check dashboard.js uses for its own loads.
function isLive(owner) {
  return state === owner && getNavigationId() === owner.navId;
}

// ── Labels ──

// Sprint Planning's selector rule (sprint-planning.js selectorLabel()): a custom name as-is, an
// auto-generated "Sprint N" under the project's own noun.
export function sprintOptionLabel(sprint, singular) {
  if (!sprint) return '';
  return sprint.name && !isGeneratedGroupName(sprint.name, sprint.number) ? sprint.name : `${singular} ${sprint.number}`;
}

// "Oct 6 – Oct 19 · 5 days left", or "No dates set". A closed sprint shows its range only, as on the
// sprint board: one completed early has no days left, whatever its dates say.
function scheduleLine(sprint, today) {
  const schedule = sprint ? sprintSchedule(sprint.start_date, sprint.end_date, today) : null;
  if (!schedule) return sprint ? 'No dates set' : '';
  if (sprint.status === 'closed') return schedule.range;
  return [schedule.range, schedule.label].filter(Boolean).join(' · ');
}

function formatPct(pct) {
  if (!(pct > 0)) return '0%';
  return pct < 1 ? '<1%' : `${Math.round(pct)}%`;
}

function plainCount(n, noun) {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

// ── Shared states ──

function loadingState() {
  return '<div class="insights-loading"><span class="spinner"></span></div>';
}

// `hintHtml` is markup the caller already escaped.
function messageState(message, hintHtml = '') {
  return `<div class="insights-state"><p class="insights-state-message">${escapeText(message)}</p>`
    + (hintHtml ? `<p class="insights-state-hint">${hintHtml}</p>` : '') + '</div>';
}

function errorState(message, err, action) {
  const detail = err && err.message ? `<p class="insights-state-hint">${escapeText(err.message)}</p>` : '';
  return `<div class="insights-state insights-state-error"><p class="insights-state-message">${escapeText(message)}</p>${detail}`
    + `<button class="btn btn-ghost btn-sm" type="button" data-action="${action}">Retry</button></div>`;
}

// Both sprint charts' first state.
function sprintsOffState(view) {
  const link = `<a href="#/projects/${escapeText(view.projectId)}/settings?tab=workflow">Turn them on in Settings ▸ Workflow</a>`;
  return messageState(`${view.plural} are turned off for this project.`, view.canManageSettings ? link : '');
}

// ── Burndown widget ──
//
// `view`: { projectId, singular, plural, sprintsEnabled, canWrite, canManageSettings, sprints (real
// sprints, GET /sprints order), sprintsError, selected (a row of `sprints` or null), burndown
// ({ status, data, error }), width (chart px), today, geom? (precomputed for `data` at `width`) }.

function renderSprintSelect(view) {
  if (!view.sprintsEnabled || view.sprintsError || !view.sprints.length) return '';
  const selected = view.selected ? view.selected.number : null;
  const option = s => `<option value="${escapeText(s.number)}"${s.number === selected ? ' selected' : ''}>${escapeText(sprintOptionLabel(s, view.singular))}</option>`;
  const open = view.sprints.filter(s => s.status !== 'closed');
  const closed = view.sprints.filter(s => s.status === 'closed');
  return `<select class="form-select form-select-sm insights-sprint-select" id="insights-sprint-select" aria-label="${escapeText(view.singular)}">`
    + open.map(option).join('')
    + (closed.length ? `<optgroup label="Closed">${closed.map(option).join('')}</optgroup>` : '')
    + '</select>';
}

export function burndownSubtitle(view) {
  if (!view.sprintsEnabled || view.sprintsError || !view.selected) return '';
  return scheduleLine(view.selected, view.today);
}

function summaryText(summary, scheduleLabel) {
  const amount = v => formatAmount(v, summary.unit);
  if (summary.phase === 'upcoming') return [`${amount(summary.total)} planned`, scheduleLabel].filter(Boolean).join(' · ');
  if (summary.remaining <= 0) return `All ${amount(summary.total)} done`;
  if (summary.phase === 'ended') return `Ended with ${formatValue(summary.remaining)} of ${amount(summary.total)} left`;
  const gap = formatValue(Math.abs(summary.delta));
  const versus = gap === '0' ? 'on the ideal line' : `${gap} ${summary.delta > 0 ? 'behind' : 'ahead of'} ideal`;
  return `${formatValue(summary.remaining)} of ${amount(summary.total)} left · ${versus}`;
}

function renderBurndownTable(data) {
  const unit = data.unit === 'story_points' ? 'Story points' : 'Tasks';
  const rows = data.days.map(d => `<tr><th scope="row">${escapeText(dayLabel(d.date))}</th>`
    + `<td>${escapeText(formatValue(d.ideal))}</td>`
    + `<td>${d.remaining == null ? '—' : escapeText(formatValue(d.remaining))}</td></tr>`).join('');
  return `<details class="bd-table-wrap"><summary>Data table</summary><div class="bd-table-scroll">`
    + `<table class="bd-table"><caption class="sr-only">${escapeText(unit)} remaining per day</caption>`
    + '<thead><tr><th scope="col">Day</th><th scope="col">Ideal</th><th scope="col">Remaining</th></tr></thead>'
    + `<tbody>${rows}</tbody></table></div></details>`;
}

function renderBurndownData(view, name) {
  const data = view.burndown.data;
  const geom = view.geom || buildBurndownGeometry(data, { width: view.width, today: view.today });
  const schedule = sprintSchedule(view.selected.start_date, view.selected.end_date, view.today);
  const summary = summaryText(burndownSummary(data, geom), schedule && schedule.label);
  const label = `${name} burndown, ${schedule ? schedule.range : ''}: ${summary}`;
  return `<p class="bd-summary">${escapeText(summary)}</p>`
    + '<div class="bd-legend-row"><ul class="bd-legend">'
    + '<li><svg class="bd-key" viewBox="0 0 24 8" aria-hidden="true"><line class="bd-key-remaining" x1="1" y1="4" x2="23" y2="4"/></svg>Remaining</li>'
    + '<li><svg class="bd-key" viewBox="0 0 24 8" aria-hidden="true"><line class="bd-key-ideal" x1="1" y1="4" x2="23" y2="4" stroke-dasharray="6 5"/></svg>Ideal</li>'
    + `</ul><p class="bd-readout" aria-live="polite">${escapeText(burndownReadout(geom, defaultReadoutIndex(geom)))}</p></div>`
    + `<div class="bd-chart" tabindex="0" role="group" aria-label="${escapeText(`${name} burndown chart. Use the arrow keys to read each day.`)}">${renderBurndownSvg(geom, { label })}</div>`
    + renderBurndownTable(data);
}

export function renderBurndownBody(view) {
  const base = `#/projects/${escapeText(view.projectId)}`;
  const planning = `<a href="${base}/sprint-planning">${escapeText(`${view.plural} Planning`)}</a>`;
  if (!view.sprintsEnabled) return sprintsOffState(view);
  if (view.sprintsError) return errorState(`Couldn't load ${view.plural.toLowerCase()}.`, view.sprintsError, 'retry-burndown');
  if (!view.sprints.length) {
    return messageState(`No ${view.plural.toLowerCase()} yet.`, view.canWrite ? `Plan one in ${planning}.` : '');
  }
  const sprint = view.selected;
  const name = sprintOptionLabel(sprint, view.singular);
  const block = sprintChartBlock(sprint);
  if (block === 'datesMissing') {
    return messageState(`Set a start and end date for ${name} to see its burndown.`,
      view.canWrite ? `Open ${planning}, pick ${escapeText(name)} and set its dates.` : 'Ask an editor to set its dates in Planning.');
  }
  if (block === 'tooLong') {
    return messageState(`${name} runs longer than ${MAX_CHART_DAYS} days, too long to chart.`,
      view.canWrite ? `Check its dates in ${planning}.` : '');
  }
  const { status, data, error } = view.burndown;
  if (status === 'error') return errorState("Couldn't load the burndown.", error, 'retry-burndown');
  if (status !== 'ready' || !data) return loadingState();
  if (!(data.total > 0)) {
    return messageState(data.unit === 'story_points' ? `${name}'s tasks have no story points yet.` : `${name} has no tasks yet.`);
  }
  return renderBurndownData(view, name);
}

export function renderBurndownWidget(view) {
  return '<section class="insights-widget insights-widget-burndown" id="insights-burndown" aria-labelledby="insights-burndown-title">'
    + '<header class="insights-widget-head"><div class="insights-widget-heading">'
    + '<h3 class="insights-widget-title" id="insights-burndown-title">Burndown</h3>'
    + `<p class="insights-widget-sub" id="insights-burndown-sub">${escapeText(burndownSubtitle(view))}</p></div>`
    + `<div class="insights-widget-tools" id="insights-burndown-tools">${renderSprintSelect(view)}</div></header>`
    + `<div class="insights-widget-body" id="insights-burndown-body">${renderBurndownBody(view)}</div></section>`;
}

// ── Velocity widget ──
//
// `view`: { projectId, singular, plural, sprintsEnabled, canWrite, canManageSettings, velocity
// ({ status, rows (the API's list, newest first), error }), width (chart px), geom? (precomputed for
// `rows` at `width`) }.

export function velocitySubtitle(view) {
  if (!view.sprintsEnabled || view.velocity.status !== 'ready') return '';
  const n = view.velocity.rows.length;
  if (n === 0) return '';
  return n === 1 ? `Last ${view.singular.toLowerCase()}` : `Last ${n} ${view.plural.toLowerCase()}`;
}

function velocitySummary(geom, view) {
  const noun = view.singular.toLowerCase();
  const span = geom.averageCount > 1 ? `${geom.averageCount}-${noun} average` : `1 ${noun} so far`;
  return `${formatAmount(geom.average, 'story_points')} per ${noun} · ${span}`;
}

function renderVelocityTable(view) {
  const rows = view.geom.groups.slice().reverse().map(g => `<tr><th scope="row">${escapeText(sprintOptionLabel(g, view.singular))}</th>`
    + `<td>${escapeText(formatValue(g.committed))}</td>`
    + `<td>${escapeText(formatValue(g.completed))}</td>`
    + `<td>${escapeText(formatValue(g.rolling))}</td></tr>`).join('');
  return '<details class="bd-table-wrap"><summary>Data table</summary><div class="bd-table-scroll">'
    + `<table class="bd-table"><caption class="sr-only">Story points committed and completed per ${escapeText(view.singular.toLowerCase())}, newest first</caption>`
    + `<thead><tr><th scope="col">${escapeText(view.singular)}</th><th scope="col">Committed</th><th scope="col">Completed</th><th scope="col">Average</th></tr></thead>`
    + `<tbody>${rows}</tbody></table></div></details>`;
}

function renderVelocityData(view) {
  const summary = velocitySummary(view.geom, view);
  const readout = velocityReadout(view.geom, view.geom.groups.length - 1, g => sprintOptionLabel(g, view.singular));
  return `<p class="bd-summary">${escapeText(summary)}</p>`
    + '<div class="bd-legend-row"><ul class="bd-legend">'
    + '<li><svg class="vc-key" viewBox="0 0 10 10" aria-hidden="true"><rect class="vc-committed" x="0.75" y="0.75" width="8.5" height="8.5" rx="2"/></svg>Committed</li>'
    + '<li><svg class="vc-key" viewBox="0 0 10 10" aria-hidden="true"><rect class="vc-completed" width="10" height="10" rx="2"/></svg>Completed</li>'
    + '<li><svg class="bd-key" viewBox="0 0 24 8" aria-hidden="true"><line class="vc-key-rolling" x1="1" y1="4" x2="23" y2="4"/></svg>Rolling average</li>'
    + '</ul></div>'
    + `<div class="bd-chart vc-chart" tabindex="0" role="group" aria-label="${escapeText(`Velocity chart. Use the arrow keys to read each ${view.singular.toLowerCase()}.`)}">`
    + `${renderVelocitySvg(view.geom, { label: `Velocity: ${summary}` })}</div>`
    + `<p class="bd-readout vc-readout" aria-live="polite">${escapeText(readout)}</p>`
    + renderVelocityTable(view);
}

export function renderVelocityBody(view) {
  if (!view.sprintsEnabled) return sprintsOffState(view);
  const { status, rows, error } = view.velocity;
  if (status === 'error') return errorState("Couldn't load velocity.", error, 'retry-velocity');
  if (status !== 'ready') return loadingState();
  const lower = view.plural.toLowerCase();
  if (!rows.length) {
    const planning = `<a href="#/projects/${escapeText(view.projectId)}/sprint-planning">${escapeText(`${view.plural} Planning`)}</a>`;
    return messageState(`No finished ${lower} to measure yet.`,
      view.canWrite ? `Velocity counts ${escapeText(lower)} started and completed in ${planning}.` : `Velocity counts ${escapeText(lower)} that were started and completed.`);
  }
  if (!rows.some(row => row.committed > 0 || row.completed > 0)) {
    return messageState(`Finished ${lower} have no story points yet.`,
      `Velocity counts story points, so estimate tasks before a ${escapeText(view.singular.toLowerCase())} starts.`);
  }
  return renderVelocityData({ ...view, geom: view.geom || velocityBars(rows, { width: view.width }) });
}

export function renderVelocityWidget(view) {
  return '<section class="insights-widget insights-widget-velocity" id="insights-velocity" aria-labelledby="insights-velocity-title">'
    + '<header class="insights-widget-head"><div class="insights-widget-heading">'
    + '<h3 class="insights-widget-title" id="insights-velocity-title">Velocity</h3>'
    + `<p class="insights-widget-sub" id="insights-velocity-sub">${escapeText(velocitySubtitle(view))}</p></div></header>`
    + `<div class="insights-widget-body" id="insights-velocity-body">${renderVelocityBody(view)}</div></section>`;
}

// ── Status widget ──

// One row per status in the project's workflow order, then any status name the counts carry that the
// registry doesn't (a status renamed or removed while tasks kept it). facets reports the overall
// total under `all`, so a status really named "all" is whatever the other keys leave over.
// `donePct` is the dashboard's progress figure: complete-role tasks over every task that isn't
// canceled — null when the workflow has no complete role or nothing counts.
export function statusRows(statuses, counts) {
  const c = counts || {};
  const total = finite(c.all);
  const registry = (statuses || []).filter(s => s && s.name);
  const known = new Set(registry.map(s => s.name));
  const named = Object.keys(c).filter(k => k !== 'all');
  const countOf = (name) => {
    if (name !== 'all') return finite(c[name]);
    return Math.max(0, total - named.reduce((sum, k) => sum + finite(c[k]), 0));
  };
  const rows = [
    ...registry.map(s => ({
      name: s.name,
      color: s.color,
      count: countOf(s.name),
      complete: !!s.is_workflow_complete,
      canceled: !!s.is_workflow_canceled,
    })),
    ...named.filter(k => !known.has(k)).map(name => ({ name, color: null, count: countOf(name), complete: false, canceled: false })),
  ];
  for (const row of rows) row.pct = total > 0 ? (row.count / total) * 100 : 0;
  const sum = (pick) => rows.filter(pick).reduce((s, r) => s + r.count, 0);
  const base = total - sum(r => r.canceled);
  return {
    rows,
    total,
    donePct: rows.some(r => r.complete) && base > 0 ? (sum(r => r.complete) / base) * 100 : null,
  };
}

function finite(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// `--status-color` for a row; only a bare palette token name reaches the style attribute.
function statusColorStyle(row) {
  const color = typeof row.color === 'string' && /^[a-z0-9]+$/.test(row.color) ? row.color : null;
  return statusBadgeAttrs({ color }).style;
}

function renderStatusBody(summary) {
  const live = summary.rows.filter(r => r.count > 0);
  const bar = live.map(r => `<span class="insights-status-seg" style="${escapeText(`${statusColorStyle(r)};flex-grow:${r.count}`)}" `
    + `title="${escapeText(`${statusLabel(r.name)}: ${r.count} (${formatPct(r.pct)})`)}"></span>`).join('');
  const barLabel = live.map(r => `${statusLabel(r.name)} ${r.count}`).join(', ');
  const list = summary.rows.map(r => `<li class="insights-status-row${r.count > 0 ? '' : ' is-empty'}">`
    + `<span class="insights-status-dot" style="${escapeText(statusColorStyle(r))}" aria-hidden="true"></span>`
    + `<span class="insights-status-name">${escapeText(statusLabel(r.name))}</span>`
    + `<span class="insights-status-count">${escapeText(r.count)}</span>`
    + `<span class="insights-status-pct">${escapeText(formatPct(r.pct))}</span></li>`).join('');
  return `<div class="insights-status-bar" role="img" aria-label="${escapeText(barLabel)}">${bar}</div>`
    + `<ul class="insights-status-list">${list}</ul>`;
}

// `view`: { summary (statusRows() result) or null while loading, error }.
export function renderStatusWidget({ summary = null, error = null } = {}) {
  let sub = '';
  let body;
  if (error) {
    body = errorState("Couldn't load task counts.", error, 'retry-status');
  } else if (!summary) {
    body = loadingState();
  } else if (summary.total <= 0) {
    sub = 'All tasks in this project';
    body = messageState('No tasks yet.');
  } else {
    sub = `${plainCount(summary.total, 'task')} in this project`
      + (summary.donePct != null ? ` · ${formatPct(summary.donePct)} done` : '');
    body = renderStatusBody(summary);
  }
  return '<section class="insights-widget insights-widget-status" id="insights-status" aria-labelledby="insights-status-title">'
    + '<header class="insights-widget-head"><div class="insights-widget-heading">'
    + '<h3 class="insights-widget-title" id="insights-status-title">Tasks by status</h3>'
    + `<p class="insights-widget-sub">${escapeText(sub)}</p></div></header>`
    + `<div class="insights-widget-body">${body}</div></section>`;
}

// ── Page state → views ──

function singular() {
  return groupLabelSingular(state.project && state.project.task_group_label);
}

function plural() {
  return groupLabelPlural(state.project && state.project.task_group_label);
}

function chartSprints() {
  return state.sprints.filter(s => s && Number(s.number) > 0);
}

function selectedSprint() {
  return chartSprints().find(s => s.number === state.selectedNumber) || null;
}

function burndownBody() {
  return document.getElementById('insights-burndown-body');
}

function velocityBody() {
  return document.getElementById('insights-velocity-body');
}

// What both sprint widgets' views share.
function projectView() {
  return {
    projectId: state.projectId,
    singular: singular(),
    plural: plural(),
    sprintsEnabled: sprintsEnabled(state.project),
    canWrite: can(state.project, state.user, 'task:write'),
    canManageSettings: can(state.project, state.user, 'settings:manage'),
  };
}

// `withGeom` adds the chart geometry for a loaded burndown at the body's current width.
function burndownView({ withGeom = false } = {}) {
  const body = burndownBody();
  const view = {
    ...projectView(),
    sprints: chartSprints(),
    sprintsError: state.sprintsError,
    selected: selectedSprint(),
    burndown: state.burndown,
    width: (body && Math.round(body.clientWidth)) || 640,
    today: todayIso(),
  };
  const data = state.burndown.status === 'ready' ? state.burndown.data : null;
  view.geom = withGeom && data && data.total > 0 ? buildBurndownGeometry(data, { width: view.width, today: view.today }) : null;
  return view;
}

// `withGeom` adds the chart geometry for loaded rows at the body's current width.
function velocityView({ withGeom = false } = {}) {
  const body = velocityBody();
  const view = { ...projectView(), velocity: state.velocity, width: (body && Math.round(body.clientWidth)) || 320 };
  const { status, rows } = state.velocity;
  view.geom = withGeom && status === 'ready' && rows.length ? velocityBars(rows, { width: view.width }) : null;
  return view;
}

function statusView() {
  if (state.statusError) return { error: state.statusError };
  return { summary: state.statusCounts ? statusRows(state.statuses, state.statusCounts) : null };
}

// ── Burndown painting and interaction ──

function paintBurndownHeader() {
  const view = burndownView();
  const sub = document.getElementById('insights-burndown-sub');
  if (sub) sub.textContent = burndownSubtitle(view);
  const tools = document.getElementById('insights-burndown-tools');
  if (tools) tools.innerHTML = renderSprintSelect(view);
}

function paintBurndownSub() {
  const sub = document.getElementById('insights-burndown-sub');
  if (sub) sub.textContent = burndownSubtitle(burndownView());
}

function paintBurndownBody() {
  const body = burndownBody();
  if (!body) return;
  const view = burndownView({ withGeom: true });
  state.geom = view.geom;
  state.chartWidth = view.width;
  state.hoverIndex = -1;
  body.innerHTML = renderBurndownBody(view);
  body.removeAttribute('aria-busy');
  const chart = body.querySelector('.bd-chart');
  if (chart && state.geom) bindChart(chart, burndownReader);
}

// Re-renders the SVG alone at the body's new width — the chart container (and its focus) stays.
function redrawChart() {
  const body = burndownBody();
  const chart = body && body.querySelector('.bd-chart');
  const data = state.burndown.data;
  if (!chart || !state.geom || !data) return;
  const width = Math.round(body.clientWidth);
  if (!width || Math.abs(width - state.chartWidth) < 2) return;
  state.chartWidth = width;
  state.geom = buildBurndownGeometry(data, { width, today: todayIso() });
  const svg = chart.querySelector('svg');
  const label = svg ? svg.getAttribute('aria-label') : '';
  chart.innerHTML = renderBurndownSvg(state.geom, { label });
  if (state.hoverIndex >= 0) showDay(chart, state.hoverIndex);
}

function showDay(chart, index) {
  const geom = state.geom;
  const p = geom && geom.points[index];
  const hover = chart.querySelector('.bd-hover');
  if (!p || !hover) return;
  state.hoverIndex = index;
  hover.classList.add('is-on');
  const crosshair = hover.querySelector('.bd-crosshair');
  crosshair.setAttribute('x1', p.x);
  crosshair.setAttribute('x2', p.x);
  const ideal = hover.querySelector('.bd-hover-ideal');
  ideal.setAttribute('cx', p.x);
  ideal.setAttribute('cy', p.yIdeal);
  const remaining = hover.querySelector('.bd-hover-remaining');
  remaining.classList.toggle('is-off', p.yRemaining == null);
  if (p.yRemaining != null) {
    remaining.setAttribute('cx', p.x);
    remaining.setAttribute('cy', p.yRemaining);
  }
  const readout = burndownBody()?.querySelector('.bd-readout');
  if (readout) readout.textContent = burndownReadout(geom, index);
}

function clearDay(chart) {
  state.hoverIndex = -1;
  chart.querySelector('.bd-hover')?.classList.remove('is-on');
  const readout = burndownBody()?.querySelector('.bd-readout');
  if (readout) readout.textContent = burndownReadout(state.geom, defaultReadoutIndex(state.geom));
}

// One chart's side of bindChart(): its geometry, which index is on show, which index an x position
// falls on, where the readout rests, and how to show or clear an index.
const burndownReader = {
  geom: () => state.geom,
  hovered: () => state.hoverIndex,
  count: geom => geom.points.length,
  nearest: nearestIndex,
  rest: defaultReadoutIndex,
  show: showDay,
  clear: clearDay,
};

const velocityReader = {
  geom: () => state.velocityGeom,
  hovered: () => state.velocityHover,
  count: geom => geom.groups.length,
  nearest: nearestGroup,
  rest: geom => geom.groups.length - 1,
  show: showSprint,
  clear: clearSprint,
};

// Pointer and keyboard reading of a chart. Bound on each rendered .bd-chart, so the listeners go
// with it on the next body render; `pointerleave` doesn't bubble, so it can't be delegated anyway.
function bindChart(chart, reader) {
  const indexAt = (e) => {
    const svg = chart.querySelector('svg');
    const rect = svg && svg.getBoundingClientRect();
    const geom = reader.geom();
    if (!rect || !rect.width || !geom) return -1;
    return reader.nearest(geom, ((e.clientX - rect.left) * geom.width) / rect.width);
  };
  const onPointer = (e) => {
    const index = indexAt(e);
    if (index >= 0) reader.show(chart, index);
  };
  chart.addEventListener('pointermove', onPointer);
  chart.addEventListener('pointerdown', onPointer);
  // A touch ends with pointerleave right after the tap; keep the tapped value on screen instead.
  chart.addEventListener('pointerleave', (e) => { if (e.pointerType !== 'touch') reader.clear(chart); });
  // Focus follows a click or tap too; keep the value that pointerdown just picked.
  chart.addEventListener('focus', () => { if (reader.hovered() < 0) reader.show(chart, reader.rest(reader.geom())); });
  chart.addEventListener('blur', () => reader.clear(chart));
  chart.addEventListener('keydown', (e) => {
    const geom = reader.geom();
    const last = geom ? reader.count(geom) - 1 : -1;
    if (last < 0) return;
    const at = reader.hovered() >= 0 ? reader.hovered() : reader.rest(geom);
    let next = null;
    if (e.key === 'ArrowLeft') next = Math.max(0, at - 1);
    else if (e.key === 'ArrowRight') next = Math.min(last, at + 1);
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    else if (e.key === 'Escape') { reader.clear(chart); return; }
    if (next == null) return;
    e.preventDefault();
    reader.show(chart, next);
  });
}

// ── Velocity painting and interaction ──

function sprintReadout(index) {
  return velocityReadout(state.velocityGeom, index, g => sprintOptionLabel(g, singular()));
}

function paintVelocitySub() {
  const sub = document.getElementById('insights-velocity-sub');
  if (sub) sub.textContent = velocitySubtitle(velocityView());
}

function paintVelocityBody() {
  const body = velocityBody();
  if (!body) return;
  const view = velocityView({ withGeom: true });
  state.velocityGeom = view.geom;
  state.velocityWidth = view.width;
  state.velocityHover = -1;
  body.innerHTML = renderVelocityBody(view);
  const chart = body.querySelector('.bd-chart');
  if (chart && state.velocityGeom) bindChart(chart, velocityReader);
}

// Re-renders the SVG alone at the body's new width, as redrawChart() does for the burndown.
function redrawVelocity() {
  const body = velocityBody();
  const chart = body && body.querySelector('.bd-chart');
  if (!chart || !state.velocityGeom) return;
  const width = Math.round(body.clientWidth);
  if (!width || Math.abs(width - state.velocityWidth) < 2) return;
  state.velocityWidth = width;
  state.velocityGeom = velocityBars(state.velocity.rows, { width });
  const svg = chart.querySelector('svg');
  const label = svg ? svg.getAttribute('aria-label') : '';
  chart.innerHTML = renderVelocitySvg(state.velocityGeom, { label });
  if (state.velocityHover >= 0) showSprint(chart, state.velocityHover);
}

function showSprint(chart, index) {
  const group = state.velocityGeom && state.velocityGeom.groups[index];
  const band = chart.querySelector('.vc-hover');
  if (!group || !band) return;
  state.velocityHover = index;
  band.classList.add('is-on');
  band.setAttribute('x', group.band.x);
  band.setAttribute('width', group.band.width);
  const readout = velocityBody()?.querySelector('.bd-readout');
  if (readout) readout.textContent = sprintReadout(index);
}

function clearSprint(chart) {
  state.velocityHover = -1;
  chart.querySelector('.vc-hover')?.classList.remove('is-on');
  const readout = velocityBody()?.querySelector('.bd-readout');
  if (readout && state.velocityGeom) readout.textContent = sprintReadout(velocityReader.rest(state.velocityGeom));
}

// Fetches and paints the Velocity widget. With sprints turned off there is nothing to ask for and
// its body keeps the message. Never rejects.
async function loadVelocity(owner) {
  if (!sprintsEnabled(state.project)) return;
  const res = await getSprintVelocity(state.projectId, VELOCITY_SPRINTS).catch(err => ({ error: err }));
  if (!isLive(owner)) return;
  state.velocity = res.error
    ? { status: 'error', rows: [], error: res.error }
    : { status: 'ready', rows: res.sprints || [], error: null };
  paintVelocitySub();
  paintVelocityBody();
}

function retryVelocity(owner) {
  state.velocity = { status: 'loading', rows: [], error: null };
  paintVelocityBody();
  void loadVelocity(owner);
}

// ── Burndown data ──

// Fetches and paints the selected sprint's burndown. A sprint that can't be charted paints its
// message without a request; the server's own 400 for missing dates (cleared since the list loaded)
// lands on the same message. While a new sprint loads, the previous chart stays up, faded via
// aria-busy (the board's TPT597 idiom). Never rejects.
async function loadBurndown(owner) {
  const seq = ++owner.burndownSeq;
  const sprint = selectedSprint();
  if (!sprintsEnabled(state.project) || state.sprintsError || !sprint || sprintChartBlock(sprint)) {
    state.burndown = { status: 'idle', data: null, error: null };
    paintBurndownBody();
    return;
  }
  const body = burndownBody();
  if (state.burndown.status === 'ready' && body) {
    body.setAttribute('aria-busy', 'true');
  } else {
    state.burndown = { status: 'loading', data: null, error: null };
    paintBurndownBody();
  }
  let next;
  try {
    const data = await getSprintBurndown(state.projectId, sprint.id);
    if (!isLive(owner) || seq !== owner.burndownSeq) return;
    if (data && data.sprint) {
      // The reply carries the saved row; keep the header's dates in step with the chart.
      sprint.start_date = data.sprint.start_date ?? sprint.start_date;
      sprint.end_date = data.sprint.end_date ?? sprint.end_date;
    }
    next = { status: 'ready', data, error: null };
  } catch (err) {
    if (!isLive(owner) || seq !== owner.burndownSeq) return;
    if (isSprintDatesMissing(err)) {
      sprint.start_date = null;
      sprint.end_date = null;
      next = { status: 'idle', data: null, error: null };
    } else {
      next = { status: 'error', data: null, error: err };
    }
  }
  state.burndown = next;
  paintBurndownSub();
  paintBurndownBody();
}

function selectSprint(owner, value) {
  const number = Number(value);
  if (!chartSprints().some(s => s.number === number)) return;
  state.selectedNumber = number;
  const params = getQueryParams();
  params.set('sprint', String(number));
  setQueryParams(params);
  paintBurndownSub();
  void loadBurndown(owner);
}

// `?sprint=<number>` when it names a chartable sprint, else the default pick. A value that names
// nothing here (a stale link, the board's comma list) is dropped from the URL.
function initialSprintNumber() {
  const sprints = chartSprints();
  const params = getQueryParams();
  const raw = params.get('sprint');
  if (raw != null) {
    const wanted = /^\d+$/.test(raw) ? Number(raw) : null;
    if (sprintsEnabled(state.project) && sprints.some(s => s.number === wanted)) return wanted;
    params.delete('sprint');
    setQueryParams(params);
  }
  const pick = pickCurrentSprint(sprints);
  return pick ? pick.number : null;
}

function applySprints(res) {
  state.sprintsError = res && res.error ? res.error : null;
  state.sprints = state.sprintsError ? [] : (res.sprints || []);
}

async function retryBurndown(owner) {
  const res = await api.get('/projects/' + state.projectId + '/sprints').catch(err => ({ error: err }));
  if (!isLive(owner)) return;
  applySprints(res);
  if (!chartSprints().some(s => s.number === state.selectedNumber)) {
    const pick = pickCurrentSprint(chartSprints());
    state.selectedNumber = pick ? pick.number : null;
  }
  paintBurndownHeader();
  void loadBurndown(owner);
}

function paintStatusWidget() {
  const el = document.getElementById('insights-status');
  if (el) el.outerHTML = renderStatusWidget(statusView());
}

async function retryStatus(owner) {
  state.statusError = null;
  state.statusCounts = null;
  paintStatusWidget();
  const res = await api.get('/projects/' + state.projectId + '/tasks/facets').catch(err => ({ error: err }));
  if (!isLive(owner)) return;
  state.statusError = res.error || null;
  state.statusCounts = res.error ? null : (res.status_counts || { all: 0 });
  paintStatusWidget();
}

// Delegated on the page root, which goes with the page's markup on navigation.
function bindPage(owner) {
  const root = document.getElementById('insights-page');
  if (!root) return;
  root.addEventListener('change', (e) => {
    if (isLive(owner) && e.target && e.target.id === 'insights-sprint-select') selectSprint(owner, e.target.value);
  });
  root.addEventListener('click', (e) => {
    const button = isLive(owner) && e.target && e.target.closest ? e.target.closest('[data-action]') : null;
    if (!button) return;
    if (button.dataset.action === 'retry-burndown') void retryBurndown(owner);
    else if (button.dataset.action === 'retry-velocity') retryVelocity(owner);
    else if (button.dataset.action === 'retry-status') void retryStatus(owner);
  });
}

// Redraws the charts when their cards change width (sidebar collapse, window resize, the grid
// dropping to one column), at most once per frame.
function observeChartBodies(owner) {
  const bodies = [burndownBody(), velocityBody()].filter(Boolean);
  if (!bodies.length || typeof ResizeObserver !== 'function') return;
  const observer = new ResizeObserver(() => {
    if (owner.resizeFrame) return;
    owner.resizeFrame = requestAnimationFrame(() => {
      owner.resizeFrame = 0;
      if (!isLive(owner)) return;
      redrawChart();
      redrawVelocity();
    });
  });
  bodies.forEach(body => observer.observe(body));
  owner.resizeObserver = observer;
}

function renderShell() {
  return '<div class="insights-page" id="insights-page">'
    + '<div class="page-header"><h2>Insights</h2></div>'
    + '<div class="insights-grid" id="insights-grid">'
    + `<section class="insights-widget insights-widget-burndown">${loadingState()}</section>`
    + `<section class="insights-widget insights-widget-velocity">${loadingState()}</section>`
    + `<section class="insights-widget insights-widget-status">${loadingState()}</section>`
    + '</div></div>';
}

// ── Page entry ──

export async function renderInsightsPage(params) {
  // Read before the first await: once it changes, the user navigated on and this mount stops
  // after its next await (see router.js getNavigationId()).
  const navId = getNavigationId();
  const superseded = () => getNavigationId() !== navId;
  if (!(await checkAuth()) || superseded()) return;
  resetState();
  const owner = state;
  state.navId = navId;
  state.projectId = params.id;
  state.user = getUser();

  initLayout(state.user);
  updateLayoutContext({ projectId: params.id, projectName: null, activeView: 'insights', viewLabel: 'Insights' });

  const content = getContentEl();
  content.innerHTML = renderShell();

  // Every leg settles: loadProject fails open to null, loadStatuses falls back to the legacy five,
  // and the two lists turn a failure into `{ error }` for their own widget's error state.
  const [project, sprintsRes, statuses, facetsRes] = await Promise.all([
    loadProject(params.id),
    api.get('/projects/' + params.id + '/sprints').catch(err => ({ error: err })),
    loadStatuses(params.id),
    api.get('/projects/' + params.id + '/tasks/facets').catch(err => ({ error: err })),
  ]);
  if (superseded()) return;
  state.project = project;
  applySprints(sprintsRes);
  state.statuses = statuses || [];
  state.statusError = facetsRes.error || null;
  state.statusCounts = facetsRes.error ? null : (facetsRes.status_counts || { all: 0 });
  state.selectedNumber = initialSprintNumber();

  const grid = document.getElementById('insights-grid');
  if (!grid) return;
  grid.innerHTML = renderBurndownWidget(burndownView()) + renderVelocityWidget(velocityView()) + renderStatusWidget(statusView());
  bindPage(owner);
  void loadBurndown(owner);
  void loadVelocity(owner);
  observeChartBodies(owner);

  return () => {
    // A late teardown (router.js runs it as soon as it resolves) must leave a newer mount of this
    // page alone: that mount owns the module state by then.
    if (state !== owner) return;
    if (owner.resizeObserver) owner.resizeObserver.disconnect();
    if (owner.resizeFrame) cancelAnimationFrame(owner.resizeFrame);
    resetState();
  };
}
